import { apiPath } from '../../constants';
import { axios, apiRequestOptions, transformHttpResponse, errorRequestHandler } from 'utils';

export const OPERATIONS_SESSION_UPDATED = 'OPERATIONS_SESSION_UPDATED';

/**
 * Every operational mutation answers with the AFFECTED SESSION, and the
 * reducer replaces just that one.
 *
 * This is the no-remount rule made concrete: the page never refetches the
 * floor after an action, so React re-renders one card instead of rebuilding
 * the list — scroll position, focus and any open menu survive. It is the same
 * shape the winner endpoint uses for brackets.
 */
const mutate = (path, params, body = {}) => dispatch =>
    axios(apiRequestOptions(path, body, { params }))
        .then(transformHttpResponse)
        .then(response => {
            dispatch({ type: OPERATIONS_SESSION_UPDATED, session: response.data });
            return response.data;
        })
        .catch(error => {
            dispatch(errorRequestHandler(error));
            return Promise.reject(error);
        });

/** open | pause | resume | close */
export const transitionSession = (sessionId, transition) =>
    mutate(apiPath.OPERATIONS.SESSION_TRANSITION, { id: sessionId, transition });

/** call-up | start | finish | skip | requeue */
export const transitionQueueItem = (itemId, transition) =>
    mutate(apiPath.OPERATIONS.ITEM_TRANSITION, { id: itemId, transition });

/** «Сітка на татамі» on or off (4 Nov 2026) — answers with the mat. */
export const setBracketAtMat = (itemId, atMat) =>
    mutate(apiPath.OPERATIONS.BRACKET_AT_MAT, { id: itemId }, { atMat });

/**
 * THE FIGHT TO RUN NOW, out of the bracket's order (7 Oct 2026). Answers with
 * the mat's workspace, not a session, so it does not go through `mutate` —
 * the caller re-reads the mat.
 */
export const chooseCurrentFight = (sessionId, round, pair) => dispatch =>
    axios(apiRequestOptions(apiPath.OPERATIONS.CURRENT_FIGHT, { round, pair }, { params: { id: sessionId } }))
        .then(transformHttpResponse)
        .then(response => response.data)
        .catch(error => {
            dispatch(errorRequestHandler(error));
            return Promise.reject(error);
        });

export const reorderQueue = (sessionId, itemIds) =>
    mutate(apiPath.OPERATIONS.QUEUE_ORDER, { id: sessionId }, { itemIds });

/**
 * Moving a category is the ONE action that changes two mats, so the endpoint
 * answers with BOTH — the mat it left and the mat it went to — and each is
 * replaced in the store.
 *
 * Dispatching the same OPERATIONS_SESSION_UPDATED twice rather than inventing
 * a plural action: the reducer's contract is "replace this session, leave its
 * siblings identical by reference", and that is exactly what is wanted here,
 * twice. A bulk action would have to re-implement it.
 *
 * With only the target replaced — which is what this used to do — the source
 * mat kept the category it had just lost, so the floor showed it on two mats
 * at once and the operator saw no result from the menu they had just used.
 */
export const moveQueueItem = (itemId, targetSessionId) => dispatch =>
    axios(apiRequestOptions(apiPath.OPERATIONS.ITEM_MOVE, { targetSessionId }, { params: { id: itemId } }))
        .then(transformHttpResponse)
        .then(response => {
            const sessions = Array.isArray(response.data) ? response.data : [response.data];

            sessions.forEach(session => dispatch({ type: OPERATIONS_SESSION_UPDATED, session }));

            return sessions;
        })
        .catch(error => {
            dispatch(errorRequestHandler(error));
            return Promise.reject(error);
        });

/**
 * Put a secretary on a mat, or clear it by passing null.
 *
 * Answers with the affected session like every other mutation here, so the
 * one card re-renders and the floor is never refetched.
 */
// The WHOLE set of the mat's secretaries — several, equal rights (6 Oct 2026).
export const assignSecretary = (sessionId, competitionSecretaryIds) =>
    mutate(apiPath.OPERATIONS.ASSIGN_SECRETARY, { id: sessionId }, { competitionSecretaryIds });

/** The mat's referee and arbiter (канса) — each a competition judge's id, or null (17 Oct 2026). */
export const assignJudges = (sessionId, refereeId, arbiterId) =>
    mutate(apiPath.OPERATIONS.ASSIGN_JUDGES, { id: sessionId }, { refereeId, arbiterId });

export const provisionOperations = competitionId =>
    dispatch =>
        axios(apiRequestOptions(apiPath.OPERATIONS.PROVISION, {}, { params: { competitionId } }))
            .then(transformHttpResponse)
            .then(response => {
                dispatch({ type: 'OPERATIONS_SUCCESS', competitionId, data: response.data });
                return response.data;
            })
            .catch(error => {
                dispatch(errorRequestHandler(error));
                return Promise.reject(error);
            });

/**
 * «УЗГОДИТИ З РОЗКЛАДОМ» (organiser, 3 Nov 2026): the waiting categories to
 * the mat and the order the current schedule gives them — the server leaves
 * what has started, run or been skipped. The whole floor comes back; resolves
 * with what was done ({moved, reordered, added}).
 */
export const syncOperationsWithSchedule = competitionId =>
    dispatch =>
        axios(apiRequestOptions(apiPath.OPERATIONS.SCHEDULE_SYNC, {}, { params: { competitionId } }))
            .then(transformHttpResponse)
            .then(response => {
                dispatch({ type: 'OPERATIONS_SUCCESS', competitionId, data: response.data.sessions });
                return response.data.summary;
            })
            .catch(error => {
                dispatch(errorRequestHandler(error));
                return Promise.reject(error);
            });
